import { 
  UnifiedProgram, 
  BBAData, 
  MastersProgram, 
  BusRoute, 
  Room,
  Department,
  DepartmentType 
} from '../types';
import { applyTimetableProfile, loadTimetableProfileControl } from './timetableProfiles';
import { TIMETABLE_FILES } from '../config/academicTerm';

export class DataProcessor {
  private static instance: DataProcessor;
  private programs: UnifiedProgram[] = [];
  private rooms: Room[] = [];
  private buses: BusRoute[] = [];
  private departments: Department[] = [];
  private initialized: boolean = false;
  private programIds = new Set<string>();

  private constructor() {
    // Don't initialize immediately - wait for async data loading
  }

  public static getInstance(): DataProcessor {
    if (!DataProcessor.instance) {
      DataProcessor.instance = new DataProcessor();
    }
    return DataProcessor.instance;
  }

  public async initializeData(): Promise<void> {
    if (this.initialized) return;
    
    
    // Clear existing data to prevent duplicates
    this.programs = [];
    this.rooms = [];
    this.buses = [];
    this.departments = [];
    this.programIds.clear();
    
    try {
      await loadTimetableProfileControl();

      // Only official files from the current release participate in live searches.
      // Previous releases stay archived until new PDFs are supplied for them.
      const [timetables, transportData] = await Promise.all([
        Promise.all(TIMETABLE_FILES.map(file => fetch(`/${file}`).then(res => res.ok ? res.json() : null))),
        fetch('/Transport.json').then(res => res.ok ? res.json() : null),
      ]);

      for (const data of timetables) {
        if (!data) continue;
        if (Array.isArray(data.programs)) this.processBBAData(data);
        else if (Array.isArray(data)) this.processMastersData(data);
        else this.processBCSData(data);
      }
      
      if (transportData) {
        this.processTransportData(transportData);
      }
      
      this.extractRooms();
      
      this.initializeDepartments();
      
      this.initialized = true;
    } catch (error) {
    }
  }

  private addProgram(program: UnifiedProgram): void {
    if (!this.programIds.has(program.id)) {
      this.programIds.add(program.id);
      this.programs.push(program);
    } else {
    }
  }

  private processBBAData(bbaData: any): void {
    if (!bbaData) return;
    const data = bbaData as BBAData;
    
    data.programs.forEach(program => {
      program.batches.forEach(batch => {
        batch.semesters.forEach(semester => {
          semester.groups.forEach(group => {
            // Normalize semester: "Semester VIII" -> "VIII"
            const normalizedSemester = semester.semester.replace(/^Semester\s+/i, '').trim();
            // Normalize group: "Group B" -> "B"
            const normalizedGroup = group.group.replace(/^Group\s+/i, '').trim();
            
            const departmentName = this.getDepartmentFromProgram(program.program_name);
            const { schedule: profiledSchedule } = applyTimetableProfile(group.schedule, {
              institute: 'Institute of Management Sciences',
              department: departmentName,
              program: program.program_name
            });

            const unifiedProgram: UnifiedProgram = {
              id: `bba-${program.program_name}-${batch.batch_session}-${normalizedSemester}-${normalizedGroup}`,
              program: program.program_name,
              department: departmentName,
              years: batch.batch_session,
              semester: normalizedSemester,
              group: normalizedGroup,
              effective_date: group.effective_date,
              schedule: profiledSchedule
            };
            this.addProgram(unifiedProgram);
          });
        });
      });
    });
  }

  private processBCSData(bcsData: any): void {
    if (!bcsData) return;
    const data = bcsData as any;
    
    // Handle the nested object structure: program -> years -> semester -> group -> days
    Object.entries(data).forEach(([programName, programData]: [string, any]) => {
      Object.entries(programData).forEach(([years, yearData]: [string, any]) => {
        Object.entries(yearData).forEach(([semesterName, semesterData]: [string, any]) => {
          Object.entries(semesterData).forEach(([groupName, groupData]: [string, any]) => {
            // Convert the schedule format
            const schedule: { [day: string]: any[] } = {};
            
            Object.entries(groupData).forEach(([day, courses]: [string, any]) => {
              if (Array.isArray(courses)) {
                schedule[day] = courses.map(course => ({
                  course_name: course.course,
                  instructor: course.instructor,
                  time: `${course.startTime}-${course.endTime}`,
                  location: course.location
                }));
              }
            });
            
            // Extract semester number from name (e.g., "Semester I" -> "I")
            const semester = semesterName.replace('Semester ', '');
            // Extract group letter from name (e.g., "Group A" -> "A")
            const group = groupName.replace('Group ', '');

            const { schedule: profiledSchedule } = applyTimetableProfile(schedule, {
              institute: 'Institute of Management Sciences',
              department: 'Computer Science',
              program: programName
            });

            const unifiedProgram: UnifiedProgram = {
              id: `bcs-${programName.toLowerCase().replace(/\s+/g, '-')}-${years}-${semester}-${group}`,
              program: programName,
              department: 'Computer Science',
              years: years,
              semester: semester,
              group: group,
              schedule: profiledSchedule
            };
            this.addProgram(unifiedProgram);
          });
        });
      });
    });
  }

  private processMastersData(mastersData: any): void {
    if (!mastersData) return;
    const data = mastersData as MastersProgram[];
    
    data.forEach(program => {
      // Convert Masters schedule format to unified format
      const schedule: { [day: string]: any[] } = {};
      
      Object.entries(program.timetable).forEach(([day, courses]) => {
        schedule[day] = courses.map(course => ({
          course_name: course.course,
          instructor: course.teacher,
          time: course.time,
          location: course.location
        }));
      });

      const departmentName = this.getDepartmentFromProgram(program.program);

      const { schedule: profiledSchedule } = applyTimetableProfile(schedule, {
        institute: 'Institute of Management Sciences',
        department: departmentName,
        program: program.program
      });

      const unifiedProgram: UnifiedProgram = {
        id: `masters-${program.program}-${program.years}-${program.semester}-${program.group}`,
        program: program.program,
        department: departmentName,
        years: program.years,
        semester: program.semester,
        group: program.group,
        effective_date: program.effective_date,
        schedule: profiledSchedule
      };
      this.addProgram(unifiedProgram);
    });
  }

  private processTransportData(transportData: any): void {
    if (!transportData) return;
    this.buses = transportData as BusRoute[];
  }

  private extractRooms(): void {
    const roomsMap = new Map<string, Room>();

    // Add predefined special rooms
    const predefinedRooms: Room[] = [
      {
        id: 'library',
        name: 'Library',
        location: 'Library - 3rd Floor',
        floor: '3rd Floor',
        type: 'library'
      },
      {
        id: 'lincoln-corner',
        name: 'Lincoln Corner',
        location: 'Lincoln Corner - 3rd Floor',
        floor: '3rd Floor',
        type: 'special'
      }
    ];

    // Add predefined rooms first
    predefinedRooms.forEach(room => {
      roomsMap.set(room.id, room);
    });

    this.programs.forEach(program => {
      Object.values(program.schedule).forEach(courses => {
        courses.forEach(course => {
          if (course.location && course.location !== 'Online Class' && course.location !== 'Online Class - Online Class') {
            const locationKey = course.location.trim();
            
            const room = this.parseLocation(locationKey);
            if (room) { // Only add if room is valid (not null)
              // Use the normalized room ID as the key to prevent duplicates
              const normalizedKey = this.normalizeRoomKey(locationKey);
              
              if (!roomsMap.has(normalizedKey)) {
                roomsMap.set(normalizedKey, room);
              }
            }
          }
        });
      });
    });

    this.rooms = Array.from(roomsMap.values());
  }

  private normalizeRoomKey(location: string): string {
    // Extract the actual room identifier from various formats
    // Examples: "Basement Class - LR-B-01" -> "lr-b-01"
    //          "Ground - Class - LR-006" -> "lr-006"
    //          "1st Floor - Lab - Computer Lab FF-02" -> "computer-lab-ff-02"
    
    const locationLower = location.toLowerCase();
    
    // Extract room number/identifier patterns
    const patterns = [
      /lr-[a-z]-\d+/,  // LR-B-01, LR-006, etc.
      /lt-\d+/,        // LT-004, LT-011, etc.
      /lt-[a-z]-\d+/,  // LT-B-04, etc.
      /computer lab [a-z]+-\d+/, // Computer Lab FF-02
      /media lab computer lab \d+/, // Media Lab Computer Lab 02
      /general \d+/,   // General 2
    ];
    
    for (const pattern of patterns) {
      const match = locationLower.match(pattern);
      if (match) {
        return match[0].replace(/\s+/g, '-');
      }
    }
    
    // For special rooms or rooms without clear patterns, use the full normalized location
    return locationLower
      .replace(/\s*-\s*/g, '-')  // Normalize dashes
      .replace(/\s+/g, '-')     // Replace spaces with dashes
      .replace(/-+/g, '-')      // Remove multiple consecutive dashes
      .replace(/^-|-$/g, '');   // Remove leading/trailing dashes
  }

  private parseLocation(location: string): Room | null {
    const id = this.normalizeRoomKey(location);
    let floor: string | null = null;
    let type: Room['type'] = 'classroom';
    const locationLower = location.toLowerCase();

    // Determine floor
    if (location.includes('Ground')) floor = 'Ground Floor';
    else if (location.includes('Basement')) floor = 'Basement';
    else if (location.includes('1st Floor')) floor = '1st Floor';
    else if (location.includes('2nd Floor')) floor = '2nd Floor';
    else if (location.includes('3rd Floor')) floor = '3rd Floor';
    
    // The new timetable names a separate RSB venue without a floor number.
    else if (location.includes('RSB Block')) floor = 'RSB Block';

    // Skip rooms with unknown/unspecified floors
    if (!floor) return null;
    
    // Determine type with more comprehensive logic
    if (locationLower.includes('online')) {
      type = 'online';
    } else if (locationLower.includes('auditorium') || locationLower.includes('lt-') || locationLower.includes('convocation hall')) {
      type = 'auditorium';
    } else if (locationLower.includes('lab') || 
               locationLower.includes('computer lab') || 
               locationLower.includes('media lab')) {
      type = 'lab';
    } else {
      // Default to classroom for locations with "class" or standard room patterns
      type = 'classroom';
    }

    // Create a cleaner room name
    const roomName = this.generateCleanRoomName(location, floor, type);

    return {
      id,
      name: roomName,
      location,
      floor,
      type
    };
  }

  private generateCleanRoomName(location: string, _floor: string, _type: Room['type']): string {
    const locationLower = location.toLowerCase();
    
    // Extract room identifiers for cleaner names
    const lrMatch = locationLower.match(/lr-[a-z]?-?\d+/);
    const ltMatch = locationLower.match(/lt-[a-z]?-?\d+/);
    const computerLabMatch = locationLower.match(/computer lab [a-z]+-\d+/);
    const mediaLabMatch = locationLower.match(/media lab computer lab \d+/);
    const generalMatch = locationLower.match(/general \d+/);
    
    if (lrMatch) {
      return lrMatch[0].toUpperCase().replace(/-/g, '-');
    } else if (ltMatch) {
      return ltMatch[0].toUpperCase().replace(/-/g, '-');
    } else if (computerLabMatch) {
      return computerLabMatch[0]
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    } else if (mediaLabMatch) {
      return 'Media Lab Computer Lab ' + mediaLabMatch[0].match(/\d+/)?.[0];
    } else if (generalMatch) {
      return 'General Lab ' + generalMatch[0].match(/\d+/)?.[0];
    }
    
    // Fallback: use the original location but clean it up
    return location
      .replace(/\s*-\s*/g, ' - ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private getDepartmentFromProgram(program: string): string {
    const programLower = program.toLowerCase();
    
    if (programLower.includes('computer science')) {
      return 'Computer Science';
    } else if (programLower.includes('data science')) {
      return 'Data Science';
    } else if (programLower.includes('software engineering')) {
      return 'Software Engineering';
    } else if (programLower.includes('cyber security') || programLower.includes('cybersecurity')) {
      return 'Cyber Security';
    } else if (programLower.includes('artificial intelligence')) {
      return 'Artificial Intelligence';
    } else if (programLower.includes('accounting')) {
      return 'Accounting & Finance';
    } else if (programLower.includes('business administration')) {
      return 'Business Administration';
    } else if (programLower.includes('business analytics')) {
      return 'Business Analytics';
    } else if (programLower.includes('english')) {
      return 'English';
    } else if (programLower.includes('economics')) {
      return 'Economics';
    } else if (programLower.includes('psychology')) {
      return 'Psychology';
    } else if (programLower.includes('social sciences')) {
      return 'Social Sciences';
    } else if (programLower.includes('project management')) {
      return 'Project Management';
    } else if (programLower.includes('islamic') && programLower.includes('finance')) {
      return 'Islamic Finance';
    } else if (programLower.includes('governance') || programLower.includes('public policy')) {
      return 'Governance & Public Policy';
    } else if (programLower.includes('management')) {
      return 'Management';
    }
    
    return 'Unknown Department';
  }

  private initializeDepartments(): void {
    const departmentMap = new Map<string, Set<string>>();
    
    this.programs.forEach(program => {
      if (!departmentMap.has(program.department)) {
        departmentMap.set(program.department, new Set());
      }
      departmentMap.get(program.department)!.add(program.program);
    });

    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
    ];

    let colorIndex = 0;
    this.departments = Array.from(departmentMap.entries()).map(([name, programs]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and') as DepartmentType,
      name,
      programs: Array.from(programs),
      color: colors[colorIndex++ % colors.length]
    }));
  }

  // Public methods to access data
  public getAllPrograms(): UnifiedProgram[] {
    return [...this.programs];
  }

  public getAllRooms(): Room[] {
    return [...this.rooms];
  }

  public getAllBuses(): BusRoute[] {
    return [...this.buses];
  }

  public getAllDepartments(): Department[] {
    return [...this.departments];
  }

  public getProgramsByDepartment(department: string): UnifiedProgram[] {
    return this.programs.filter(program => 
      program.department.toLowerCase() === department.toLowerCase()
    );
  }

  public searchPrograms(query: string): UnifiedProgram[] {
    if (!query || query.trim().length < 1) return [];
    
    const queryLower = query.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/);
    
    const results: { program: UnifiedProgram; score: number }[] = [];
    
    this.programs.forEach(program => {
      let score = 0;
      
      // Program-level matching with scoring
      const programFields = [
        { field: program.program.toLowerCase(), weight: 10 },
        { field: program.department.toLowerCase(), weight: 8 },
        { field: program.years.toLowerCase(), weight: 5 },
        { field: program.semester.toLowerCase(), weight: 5 },
        { field: program.group.toLowerCase(), weight: 3 }
      ];
      
      programFields.forEach(({ field, weight }) => {
        // Exact match bonus
        if (field === queryLower) {
          score += weight * 3;
        }
        // Starts with query
        else if (field.startsWith(queryLower)) {
          score += weight * 2;
        }
        // Contains all words
        else if (queryWords.every(word => field.includes(word))) {
          score += weight * 1.5;
        }
        // Contains query
        else if (field.includes(queryLower)) {
          score += weight;
        }
        // Partial word matching
        else {
          queryWords.forEach(word => {
            if (word.length >= 2 && field.includes(word)) {
              score += weight * 0.5;
            }
          });
        }
      });
      
      // Course-level matching
      Object.values(program.schedule).forEach(courses => {
        courses.forEach(course => {
          const courseFields = [
            { field: (course.course_name || course.course || '').toLowerCase(), weight: 7 },
            { field: (course.instructor || course.teacher || '').toLowerCase(), weight: 6 },
            { field: (course.location || '').toLowerCase(), weight: 4 }
          ];
          
          courseFields.forEach(({ field, weight }) => {
            if (!field) return;
            
            // Exact match
            if (field === queryLower) {
              score += weight * 3;
            }
            // Starts with query
            else if (field.startsWith(queryLower)) {
              score += weight * 2;
            }
            // Contains query (important for course names like "Database Systems")
            else if (field.includes(queryLower)) {
              score += weight * 1.5;
            }
            // Word matching for multi-word searches (e.g., "database systems")
            else if (queryWords.length > 1 && queryWords.every(word => field.includes(word))) {
              score += weight * 1.8;
            }
            // Individual word matching
            else {
              queryWords.forEach(word => {
                if (word.length >= 2) {
                  if (field.includes(word)) {
                    // Higher score for course names and locations
                    if (weight >= 6) { // course names and teachers
                      score += weight * 0.8;
                    } else { // locations
                      score += weight * 0.6;
                    }
                  }
                  
                  // Special handling for teacher names (e.g., "Ali" matches "Dr. Ali Ahmad")
                  if (field.includes(' ')) {
                    const nameParts = field.split(/\s+/);
                    nameParts.forEach(part => {
                      if (part.startsWith(word) || (part.length > 2 && part.includes(word))) {
                        score += weight * 0.7;
                      }
                    });
                  }
                }
              });
            }
          });
        });
      });
      
      // Only include results with meaningful scores
      if (score > 0.5) {
        results.push({ program, score });
      }
    });
    
    // Sort by score (highest first) and return programs
    return results
      .sort((a, b) => b.score - a.score)
      .map(result => result.program);
  }

  public searchRooms(query: string): Room[] {
    const queryLower = query.toLowerCase();
    return this.rooms.filter(room =>
      room.name.toLowerCase().includes(queryLower) ||
      room.location.toLowerCase().includes(queryLower) ||
      room.floor.toLowerCase().includes(queryLower) ||
      room.type.toLowerCase().includes(queryLower)
    );
  }

  public getProgramById(id: string): UnifiedProgram | null {
    return this.programs.find(program => program.id === id) || null;
  }

  public getTeachers(): string[] {
    const teacherSet = new Set<string>();
    
    this.programs.forEach(program => {
      Object.values(program.schedule).forEach(courses => {
        courses.forEach(course => {
          const teacher = course.instructor || course.teacher;
          if (teacher && teacher !== 'Dummy Teacher') {
            teacherSet.add(teacher);
          }
        });
      });
    });

    return Array.from(teacherSet).sort();
  }

  public getCourses(): string[] {
    const courseSet = new Set<string>();
    
    this.programs.forEach(program => {
      Object.values(program.schedule).forEach(courses => {
        courses.forEach(course => {
          const courseName = course.course_name || course.course;
          if (courseName) {
            courseSet.add(courseName);
          }
        });
      });
    });

    return Array.from(courseSet).sort();
  }

  public searchByTeacher(teacherName: string): UnifiedProgram[] {
    const teacherLower = teacherName.toLowerCase().trim();
    console.log('Searching for teacher:', teacherName, 'in', this.programs.length, 'programs');
    
    // Use the exact same logic as the filter
    const results = this.programs.filter(program => {
      const matchesTeacher = Object.values(program.schedule).some(courses =>
        courses.some(course => {
          const instructor = course.instructor || course.teacher;
          const matches = instructor && instructor.toLowerCase().trim() === teacherLower;
          if (matches) {
            console.log('Found exact match:', instructor, 'in program:', program.program);
          }
          return matches;
        })
      );
      return matchesTeacher;
    });
    
    console.log('Teacher search found', results.length, 'programs');
    return results;
  }

  public searchByProgram(programName: string): UnifiedProgram[] {
    const programLower = programName.toLowerCase();
    console.log('Searching for program:', programName, 'in', this.programs.length, 'programs');
    
    // Use the exact same logic as the filter
    const results = this.programs.filter(program => {
      const matches = program.program.toLowerCase().includes(programLower);
      if (matches) {
        console.log('Found exact program match:', program.program);
      }
      return matches;
    });
    
    console.log('Program search found', results.length, 'programs');
    return results;
  }

  public searchByCourse(courseName: string): UnifiedProgram[] {
    const courseLower = courseName.toLowerCase();
    return this.programs.filter(program =>
      Object.values(program.schedule).some(courses =>
        courses.some(course => {
          const courseTitle = (course.course_name || course.course || '').toLowerCase();
          return courseTitle.includes(courseLower);
        })
      )
    );
  }
}
